import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { TestCase, EvalResult, Criterion } from "../types";
import { useEvalMutations } from "../hooks";
import {
  TestCaseDetailModal,
  ResultDetailModal,
  CreateTestCaseModal,
  RunNameModal,
  CreateCriterionModal,
} from ".";

type EvalMutations = ReturnType<typeof useEvalMutations>;

interface EvalsModalsProps {
  currentTab: string;
  selectedTestCase: TestCase | null;
  setSelectedTestCase: (tc: TestCase | null) => void;
  showCreateModal: boolean;
  setShowCreateModal: (show: boolean) => void;
  selectedResult: EvalResult | null;
  setSelectedResult: (result: EvalResult | null) => void;
  showRunNameModal: boolean;
  setShowRunNameModal: (show: boolean) => void;
  showCreateCriterionModal: boolean;
  setShowCreateCriterionModal: (show: boolean) => void;
  selectedCriterion: Criterion | null;
  setSelectedCriterion: (c: Criterion | null) => void;
  selectedTestCaseIds: string[];
  criteria: Criterion[] | undefined;
  handleRunEval: (testCaseId: string) => void;
  handleBatchRun: (runName: string, defaultName: string) => void;
  createMutation: EvalMutations["createMutation"];
  updateMutation: EvalMutations["updateMutation"];
  deleteMutation: EvalMutations["deleteMutation"];
  deleteResultMutation: EvalMutations["deleteResultMutation"];
  createCriterionMutation: EvalMutations["createCriterionMutation"];
  updateCriterionMutation: EvalMutations["updateCriterionMutation"];
}

export const EvalsModals: FC<EvalsModalsProps> = ({
  currentTab,
  selectedTestCase,
  setSelectedTestCase,
  showCreateModal,
  setShowCreateModal,
  selectedResult,
  setSelectedResult,
  showRunNameModal,
  setShowRunNameModal,
  showCreateCriterionModal,
  setShowCreateCriterionModal,
  selectedCriterion,
  setSelectedCriterion,
  selectedTestCaseIds,
  criteria,
  handleRunEval,
  handleBatchRun,
  createMutation,
  updateMutation,
  deleteMutation,
  deleteResultMutation,
  createCriterionMutation,
  updateCriterionMutation,
}) => {
  const navigate = useNavigate();

  const handleCloseTestCase = () => {
    setSelectedTestCase(null);
    navigate(`?tab=${currentTab}`, { replace: true });
  };

  const handleCloseResult = () => {
    setSelectedResult(null);
    navigate(`?tab=${currentTab}`, { replace: true });
  };

  return (
    <>
      {selectedTestCase && !showCreateModal && (
        <TestCaseDetailModal
          testCase={selectedTestCase}
          onClose={handleCloseTestCase}
          onRunTest={() => {
            handleRunEval(selectedTestCase.id);
            handleCloseTestCase();
          }}
          onEdit={() => setShowCreateModal(true)}
          onDelete={() => {
            if (window.confirm(`Are you sure you want to delete "${selectedTestCase.name}"?`)) {
              deleteMutation.mutate(selectedTestCase.id);
              handleCloseTestCase();
            }
          }}
        />
      )}

      {selectedResult && (
        <ResultDetailModal
          result={selectedResult}
          onClose={handleCloseResult}
          onDelete={() => {
            if (window.confirm(`Are you sure you want to delete this result?`)) {
              deleteResultMutation.mutate(selectedResult.id);
              setSelectedResult(null);
              navigate(`?tab=${currentTab}`, { replace: true });
            }
          }}
        />
      )}

      {showCreateModal && (
        <CreateTestCaseModal
          testCase={selectedTestCase}
          criteria={criteria}
          loading={createMutation.isPending || updateMutation.isPending}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedTestCase(null);
          }}
          onSubmit={data => {
            if (selectedTestCase) {
              updateMutation.mutate({ id: selectedTestCase.id, data });
            } else {
              createMutation.mutate(data);
            }
            setShowCreateModal(false);
            setSelectedTestCase(null);
          }}
        />
      )}

      {showCreateCriterionModal && (
        <CreateCriterionModal
          criterion={selectedCriterion}
          onClose={() => {
            setShowCreateCriterionModal(false);
            setSelectedCriterion(null);
          }}
          onSubmit={data => {
            if (selectedCriterion) {
              updateCriterionMutation.mutate(
                { id: selectedCriterion.id, data },
                {
                  onSuccess: () => {
                    setShowCreateCriterionModal(false);
                    setSelectedCriterion(null);
                  },
                }
              );
            } else {
              createCriterionMutation.mutate(data, {
                onSuccess: () => {
                  setShowCreateCriterionModal(false);
                  setSelectedCriterion(null);
                },
              });
            }
          }}
        />
      )}

      {showRunNameModal && (
        <RunNameModal
          onClose={() => setShowRunNameModal(false)}
          onSubmit={(runName, defaultName) => {
            setShowRunNameModal(false);
            handleBatchRun(runName, defaultName);
          }}
          selectedCount={selectedTestCaseIds.length}
        />
      )}
    </>
  );
};
